import { PlatformCountry } from "@dashboard/common/types";
import { OrderProduct } from "@dashboard/modules/orders/types/product-order.types";
import {
  DeliveryConfigurationData,
  DeliveryProduct,
  DeliveryProviderInfo,
  DeliveryProviderQuote,
  DeliverySizeConfig,
  DeliveryTypeConfig,
  OrderCalculation,
} from "../entities/delivery.entities";

export const DEFAULT_COLOMBIA_PREFIX = "+57";

export const parseBoldMarkedText = (
  message: string
): { text: string; bold: boolean }[] =>
  message.split("*").map((text, index) => ({
    text,
    bold: index % 2 === 1,
  }));

const isColombiaCountry = (country: PlatformCountry) => {
  const name = country.name?.toLowerCase() ?? "";
  return (
    country.prefixPhone === DEFAULT_COLOMBIA_PREFIX ||
    name.includes("colombia") ||
    name === "co"
  );
};

export const getDefaultPhonePrefix = (
  countries: PlatformCountry[],
  partnerPrefix?: string,
  partnerCountry?: PlatformCountry
): string => {
  if (partnerPrefix?.trim()) return partnerPrefix.trim();
  if (partnerCountry?.prefixPhone?.trim()) {
    return partnerCountry.prefixPhone.trim();
  }
  const colombia = countries.find(isColombiaCountry);
  return colombia?.prefixPhone?.trim() || DEFAULT_COLOMBIA_PREFIX;
};

/** Same lookup as profile FormStoreLocation: userDetail.country → catalog entry */
export const resolvePartnerCountry = (
  countries: PlatformCountry[],
  countryKey?: string
): PlatformCountry | undefined => {
  if (!countryKey?.trim()) return undefined;
  return countries.find(
    (c) => c.name === countryKey || c.id === countryKey
  );
};

export const resolvePartnerCountryCities = (
  countries: PlatformCountry[],
  countryKey?: string
): string[] => resolvePartnerCountry(countries, countryKey)?.cities ?? [];

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : null;

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

export const normalizeDeliverySize = (raw: unknown): DeliverySizeConfig | null => {
  const item = asRecord(raw);
  if (!item?.id) return null;
  return {
    id: String(item.id),
    label: String(item.label ?? item.name ?? item.id),
    helpText1: item.helpText1 ?? item.help_text_1
      ? String(item.helpText1 ?? item.help_text_1)
      : undefined,
    helpText2: item.helpText2 ?? item.help_text_2
      ? String(item.helpText2 ?? item.help_text_2)
      : undefined,
  };
};

export const normalizeDeliveryType = (raw: unknown): DeliveryTypeConfig | null => {
  const item = asRecord(raw);
  if (!item?.id) return null;
  const deliverySizes = asArray(item.deliverySizes ?? item.delivery_sizes).map(String);
  return {
    id: String(item.id),
    label: String(item.label ?? item.name ?? item.id),
    deliverySizes,
    enabled: item.enabled !== false,
    helpText: item.helpText ?? item.help_text
      ? String(item.helpText ?? item.help_text)
      : undefined,
    maxDays: typeof item.maxDays === "number" ? item.maxDays : undefined,
    defaultProviderId: item.defaultProviderId
      ? String(item.defaultProviderId)
      : undefined,
  };
};

export const normalizeDeliveryConfigurationData = (
  raw: unknown
): DeliveryConfigurationData => {
  const root = asRecord(raw);
  const value = asRecord(root?.value) ?? root ?? {};
  const typesRaw = value.deliveryTypes ?? value.delivery_types;
  const sizesRaw = value.deliverySizes ?? value.delivery_sizes;

  const types = asArray(typesRaw)
    .map(normalizeDeliveryType)
    .filter((t): t is DeliveryTypeConfig => t !== null);

  const sizes = asArray(sizesRaw)
    .map(normalizeDeliverySize)
    .filter((s): s is DeliverySizeConfig => s !== null);

  return { types, sizes };
};

export const normalizeDeliveryProviderInfo = (raw: unknown): DeliveryProviderInfo | null => {
  if (typeof raw === "string") {
    return { id: raw, name: raw };
  }
  const item = asRecord(raw);
  if (!item?.id) return null;
  return {
    id: String(item.id),
    name: item.name ? String(item.name) : undefined,
    providerId: item.providerId ? String(item.providerId) : undefined,
    icon: item.icon ? String(item.icon) : undefined,
    banner: item.banner ? String(item.banner) : undefined,
    webBanner: item.webBanner ? String(item.webBanner) : undefined,
    description: item.description ? String(item.description) : undefined,
    deliveryType: item.deliveryType ? String(item.deliveryType) : undefined,
    paymentType: item.paymentType ? String(item.paymentType) : undefined,
    descriptionTime: item.descriptionTime ? String(item.descriptionTime) : undefined,
    enabled: item.enabled !== false,
  };
};

export const normalizeProviderQuotes = (raw: unknown): DeliveryProviderQuote[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry): DeliveryProviderQuote | null => {
      const item = asRecord(entry);
      if (!item) return null;

      const providerRaw = item.provider;
      const provider = normalizeDeliveryProviderInfo(providerRaw);
      if (!provider) return null;

      const total =
        Number(item.total ?? item.price ?? item.estimatedPrice ?? 0) || 0;

      return { total, provider };
    })
    .filter((q): q is DeliveryProviderQuote => q !== null);
};

export const filterProvidersByDeliveryType = (
  providers: DeliveryProviderQuote[],
  deliveryTypeId?: string
): DeliveryProviderQuote[] => {
  if (!deliveryTypeId) return providers;
  return providers.filter((q) => q.provider.deliveryType === deliveryTypeId);
};

export const pickDefaultProvider = (
  providers: DeliveryProviderQuote[],
  defaultProviderId?: string
): DeliveryProviderQuote | null => {
  if (!providers.length) return null;
  if (defaultProviderId) {
    const match = providers.find((q) => q.provider.id === defaultProviderId);
    if (match) return match;
  }
  return providers[0];
};

export const getSizesForDeliveryType = (
  allSizes: DeliverySizeConfig[],
  deliveryType?: DeliveryTypeConfig
): DeliverySizeConfig[] => {
  if (!deliveryType?.deliverySizes?.length) return allSizes;
  const allowed = new Set(deliveryType.deliverySizes);
  return allSizes.filter((s) => allowed.has(s.id));
};

export const pickDefaultDeliveryType = (
  types: DeliveryTypeConfig[]
): DeliveryTypeConfig | undefined =>
  types.find((t) => t.enabled) ?? types[0];

export const resolveDeliveryCheckoutLabels = (
  config: DeliveryConfigurationData | null | undefined,
  calculation: OrderCalculation,
  selectedProvider?: DeliveryProviderQuote | null
) => {
  const size = config?.sizes?.find((s) => s.id === calculation.deliverySize);
  const typeId = selectedProvider?.provider?.deliveryType;
  const type = config?.types?.find((t) => t.id === typeId);

  return {
    sizeLabel: size?.label ?? calculation.deliverySize ?? "",
    typeLabel: type?.label ?? "",
  };
};

export const formatDeliveryPickupDate = (
  pickupDate?: string,
  locale?: string
): string => {
  if (!pickupDate) return "";
  const parsed = new Date(`${pickupDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return pickupDate;
  return parsed.toLocaleDateString(locale?.replace("_", "-") || "es", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const formatDeliveryAddress = (parts: {
  address?: string;
  addressDetails?: string;
  city?: string;
}): string => {
  const lines = [
    parts.address?.trim(),
    parts.addressDetails?.trim(),
    parts.city?.trim(),
  ].filter(Boolean);
  return lines.join(", ") || "—";
};

export const isExpressDeliveryType = (
  type?: DeliveryTypeConfig
): boolean => {
  if (!type) return false;
  const id = type.id.toLowerCase();
  const label = type.label.toLowerCase();
  return id.includes("express") || label.includes("express");
};

export const pickDefaultSizeForType = (
  sizes: DeliverySizeConfig[],
  deliveryType?: DeliveryTypeConfig
): string => {
  const filtered = getSizesForDeliveryType(sizes, deliveryType);
  const preferredId = deliveryType?.deliverySizes?.[0];
  if (preferredId && filtered.some((s) => s.id === preferredId)) {
    return preferredId;
  }
  return filtered[0]?.id ?? "";
};

export const getManualDeliveryProducts = (): DeliveryProduct[] => [
  { name: "Envío", value: 0, units: 1 },
];

export const mapOrderProductsToDeliveryProducts = (
  products?: OrderProduct[]
): DeliveryProduct[] => {
  if (!products?.length) return [];
  return products.map((product) => ({
    name: product.name,
    units: product.units,
    value: product.unitPrice,
    image: product.images?.[0],
  }));
};

export const resolveCalculationProductsAndTotal = (
  linkedOrder: { products?: OrderProduct[]; totalValue: number } | null
): { products: DeliveryProduct[]; totalValue: number } => {
  if (linkedOrder) {
    return {
      products: mapOrderProductsToDeliveryProducts(linkedOrder.products),
      totalValue: linkedOrder.totalValue ?? 0,
    };
  }
  return {
    products: getManualDeliveryProducts(),
    totalValue: 0,
  };
};
